import { makeAutoObservable } from 'mobx'
import { logger } from 'shared/lib'
import { Phone } from 'entities/Phone/model/Phone'
import { PhoneApi } from 'entities/Phone'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import type { IParamsCreateNumbersLandline } from 'entities/Phone/api/types'
import { type EnterNumberStore } from 'pages/settings/ui/EnterNumberCommon/store/EnterNumberStore'

export type IAuthorizationDocumentStoreConfig = {
  getPayload: () => IParamsCreateNumbersLandline
  fetchNumbersLandline: (number: string, update?: boolean) => Promise<Phone | { error: string }>
  syncData: (phone: Phone) => void
  initStep: (phone: Phone) => void
  enterNumberStore: EnterNumberStore
}

export class AuthorizationDocumentStore {
  private _intervalLOA: ReturnType<typeof setInterval> | null = null

  constructor(
    private _config: IAuthorizationDocumentStoreConfig,
    private _entities: IEntitiesStore
  ) {
    makeAutoObservable(this)
  }

  get email() {
    return this._entities.usersStore.user?.email
  }

  private get _phoneInternational() {
    return this._config.enterNumberStore.phoneInternational
  }

  sync = async () => {
    const document = await this.createDocument()

    if (!document.error) {
      await this.checkLOA()
    }
  }

  createDocument = async () => {
    try {
      const payload = this._config.getPayload()

      const response = await PhoneApi.createNumbersLandlineDocument(payload)
      const data = response.data

      if ('error' in data) {
        return {
          error: data.error,
        }
      }

      return {
        error: '',
      }
    } catch (e) {
      logger.error(e)

      return {
        error: '',
      }
    }
  }

  checkLOA = async () => {
    this._intervalLOA && clearInterval(this._intervalLOA)
    this._intervalLOA = setInterval(async () => {
      const data = await this._config.fetchNumbersLandline(this._phoneInternational, false)

      if (data instanceof Phone && !data.landline?.hasLOA) {
        this._intervalLOA && clearInterval(this._intervalLOA)
        if (data instanceof Phone) {
          this._config.syncData(data)
          this._config.initStep(data)
        }
      }
    }, 10000)
  }

  disposeStep = () => {
    this._intervalLOA && clearInterval(this._intervalLOA)
  }
}
